import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faCheckCircle } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, LoadingModule,
} from '@domain-group/suit-theme'

import type { DefaultContext } from '@contexts/pageContext'
import PageContext from '@contexts/pageContext'

import useCampaign from '@hooks/useCampaign'
import useCampaignContact from '@hooks/useCampaignContact'
import useCampaignContacts from '@hooks/useCampaignContacts'
import { generateUrls } from '@hooks/useCampaignQuote'
import useCampaignQuotes from '@hooks/useCampaignQuotes'

import defaultRequestOptions from '@sections/Client/packs/Campaign/defaultRequestOptions'

import CampaignContacts from '@components/CampaignContacts'
import CampaignContributions from '@components/CampaignContributions'
import FinanceApplications from '@components/FinanceApplications'
import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageHeader from '@components/PageHeader'
import ReconciliationTotals from '@components/ReconciliationTotals'

import type { CampaignModel } from '@models/types'

import type { PageContextPayload } from '../../index'

import ApprovedQuotes from '../ApprovedQuotes'
import CampaignContactsNotice from '../CampaignContactsNotice'
import CampaignQuotesBlankState from '../CampaignQuotesBlankState'
import CampaignTimelineNotice from '../CampaignTimelineNotice'
import PendingQuotes from '../PendingQuotes'

interface PageContentProps {
  campaign: CampaignModel,
}

const PageContent = (props: PageContentProps) => {
  const { campaign } = props
  const { address } = campaign || {}

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  // const { queryParamName } = parsePermittedQueryParams(getQueryParamsFromHash(), ['hidden'])

  const campaignPayload = useCampaign(campaign)
  const {
    callbacks: {
      editCampaignDates,
    },
    campaignStartDate,
    loading: loadingCampaign,
  } = campaignPayload

  const campaignContactPayload = useCampaignContact({ campaign_id: campaign.id })
  const {
    callbacks: {
      createCampaignContactModal,
    },
  } = campaignContactPayload

  const campaignContactsPayload = useCampaignContacts({
    filters: {
      campaign_id: campaign.id,
    },
    performHttpRequests: !!campaign.id,
    requestOptions: { include_campaign_contact_contact: true },
  })

  const { hasFilteredCampaignContacts, loading: loadingCampaignContacts } = campaignContactsPayload

  const campaignQuotesPayload = useCampaignQuotes({
    filters: {
      campaign_id: campaign.id,
    },
    performHttpRequests: !!campaign.id,
    requestOptions: defaultRequestOptions.campaignQuote,
  })

  const {
    hasFilteredCampaignQuotes,
    loading,
  } = campaignQuotesPayload

  // @ts-expect-error TS2345
  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      showNewCampaignQuoteModal,
    },
  } = pageContext

  const { campaignQuotesIndexUrl } = generateUrls(undefined, {
    prefixRoute: `campaigns/${campaign.id}/edit`,
  })

  const quoteRequirementsMet = campaignStartDate && hasFilteredCampaignContacts

  return (
    <>
      <Helmet>
        <title>{address || 'Campaign'} | Quotes</title>
      </Helmet>

      <PageHeader
        actionContent={
          quoteRequirementsMet ? (
            <Button
              buttonStyle="primaryCreate"
              onClick={() => showNewCampaignQuoteModal()}
              size="medium"
            >
              Create Quote
            </Button>
          ) : (
            <Button disabled size="medium">
              {!campaignStartDate ? 'Add Dates' : 'Add Vendors'}
            </Button>
          )
        }
        activeTabBarItemKey="current"
        nestedNavigation
        tabBarItems={[
          {
            href: campaignQuotesIndexUrl,
            icon: faCheckCircle,
            key: 'current',
            title: 'Quotes',
          },
        ]}
        title="Quotes"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.campaign.quotes.banner" />

            <CampaignTimelineNotice
              boxProps={{ marginBottom: 'large' }}
              // @ts-expect-error TS2322
              callbacks={{ editCampaignDates }}
              campaignStartDate={campaignStartDate}
              loading={loadingCampaign}
            />

            {!!campaignStartDate && !hasFilteredCampaignContacts && !loadingCampaignContacts && (
              <CampaignContactsNotice
                boxProps={{ marginBottom: 'large' }}
                callbacks={{ createCampaignContactModal }}
              />
            )}

            <LoadingModule loading={loading} times={3} />

            {!loading && (
              <>
                <PendingQuotes campaign={campaign} />
                <ApprovedQuotes campaign={campaign} />
              </>
            )}

            {!loading && !hasFilteredCampaignQuotes && (
              <CampaignQuotesBlankState
                campaignStartDate={campaignStartDate}
                hasContacts={hasFilteredCampaignContacts}
              />
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.campaign.quotes.sidebar" />

            {!loadingCampaign && (
              <>
                <ReconciliationTotals
                  campaign={campaign}
                  displayKeys={[
                    'approved_quote_total', 'total_combined_spend', 'contribution_total',
                  ]}
                />

                <CampaignContacts
                  campaignContactsPayload={campaignContactsPayload}
                  campaignId={campaign.id}
                />

                <CampaignContributions campaign={campaign} />

                <FinanceApplications campaignId={campaign.id} />
              </>
            )}
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
